import { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";

const COLUMNS = [
  "Family Name",
  "Kills",
  "Deaths",
  "Kill Streak",
  "Damage Done",
  "Damage Taken",
  "CC's",
  "HP Healed",
  "Ally HP Healed",
  "Structure Damage",
  "Cannons Hit",
  "Cannons Destroyed",
  "Max Cannon Hit Distance",
  "Traps Triggered",
  "Time Dead",
  "Time Survived",
];

const NUMERIC_COLUMNS = COLUMNS.slice(1);

const MAGNITUDE_COLUMNS = new Set([
  "Damage Done",
  "Damage Taken",
  "HP Healed",
  "Ally HP Healed",
  "Structure Damage",
]);

const HIDDEN_TIME_NOISE = [
  "193969",
  "493969",
  "493921",
  "93969",
  "93921",
];

const COLUMN_CENTERS = [
  0.07, // Family Name
  0.195, // Kills
  0.250, // Deaths
  0.305, // Kill Streak
  0.365, // Damage Done
  0.420, // Damage Taken
  0.475, // CC's
  0.525, // HP Healed
  0.580, // Ally HP Healed
  0.635, // Structure Damage
  0.690, // Cannons Hit
  0.745, // Cannons Destroyed
  0.800, // Max Cannon Hit Distance
  0.855, // Traps Triggered
  0.915, // Time Dead
  0.965, // Time Survived
];

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);

    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;

    document.body.appendChild(script);
  });
}

function waitForOpenCV() {
  return new Promise((resolve, reject) => {
    const started = Date.now();

    const interval = setInterval(() => {
      if (window.cv && window.cv.Mat) {
        clearInterval(interval);
        resolve();
      }

      if (Date.now() - started > 30000) {
        clearInterval(interval);
        reject(new Error("OpenCV.js failed to load within 30 seconds."));
      }
    }, 100);
  });
}

function waitForOrt() {
  return new Promise((resolve, reject) => {
    const started = Date.now();

    const interval = setInterval(() => {
      if (window.ort) {
        clearInterval(interval);
        resolve(window.ort);
      }

      if (Date.now() - started > 30000) {
        clearInterval(interval);
        reject(new Error("ONNX Runtime Web failed to load within 30 seconds."));
      }
    }, 100);
  });
}

export default function NodeWarOCR() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("Loading PaddleOCR...");
  const [processing, setProcessing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [debugText, setDebugText] = useState("");
  const [rawDebugText, setRawDebugText] = useState("");
  const [showDebug, setShowDebug] = useState(true);
  const [ocrReady, setOcrReady] = useState(false);

  const paddleRef = useRef(null);

  const loadPaddleOCR = useCallback(async () => {
    try {
      setStatus("Loading ONNX Runtime Web...");

      await loadScript(
        "https://cdn.jsdelivr.net/npm/onnxruntime-web/dist/ort.min.js"
      );

      const ort = await waitForOrt();
      ort.env.wasm.wasmPaths =
        "https://cdn.jsdelivr.net/npm/onnxruntime-web/dist/";

      setStatus("Loading OpenCV.js...");

      await loadScript("https://docs.opencv.org/4.8.0/opencv.js");
      await waitForOpenCV();

      setStatus("Loading PaddleOCR module...");

      const Paddle = await import(
        /* webpackIgnore: true */
        "https://cdn.jsdelivr.net/npm/esearch-ocr@5.1.5/dist/esearch-ocr.js"
      );

      const assetsPath = "https://cdn.jsdelivr.net/npm/paddleocr-browser/dist/";

      setStatus("Loading PaddleOCR dictionary...");

      const dictionaryResponse = await fetch(`${assetsPath}ppocr_keys_v1.txt`);
      const dictionary = await dictionaryResponse.text();

      setStatus("Initializing PaddleOCR models...");

      await Paddle.init({
        detPath: `${assetsPath}ppocr_det.onnx`,
        recPath: `${assetsPath}ppocr_rec.onnx`,
        dic: dictionary,
        ort,
        node: false,
        cv: window.cv,
      });

      paddleRef.current = Paddle;

      setOcrReady(true);
      setStatus("PaddleOCR is ready. Upload screenshots to begin.");
    } catch (error) {
      console.error(error);
      setStatus("Failed to load PaddleOCR. Check the browser console.");
    }
  }, []);

  useEffect(() => {
    loadPaddleOCR();
  }, [loadPaddleOCR]);

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (!ocrReady || !paddleRef.current) {
      alert("PaddleOCR is still loading. Try again in a moment.");
      return;
    }

    setProcessing(true);
    setStatus(`Processing ${files.length} screenshot(s)...`);
    setRows([]);
    setDebugText("");
    setRawDebugText("");
    setPreviewImage(null);

    const unique = new Map();
    let totalParsedRows = 0;

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        setStatus(`Reading ${file.name} (${i + 1}/${files.length})...`);

        const dataUrl = await fileToDataUrl(file);
        const processedCanvas = await prepareImageForOCR(dataUrl);
        const processedDataUrl = processedCanvas.toDataURL("image/png");

        setPreviewImage(processedDataUrl);

        const ocrResult = await paddleRef.current.ocr(processedDataUrl);
        const rawResultText = safeStringify(ocrResult);

        const ocrItems = removeBadLargeOcrBoxes(
          dedupeOcrItems(extractPaddleItems(ocrResult))
        );

        const tokenItems = explodeItemsIntoTokens(
          ocrItems,
          processedCanvas.width
        );

        const parsedRows = buildRowsFromPositionedTokens(
          tokenItems,
          processedCanvas.width
        );

        totalParsedRows += parsedRows.length;

        const debugGridText = parsedRows
          .map((row) => COLUMNS.map((column) => row[column] || "").join(" | "))
          .join("\n");

        console.log("PADDLE OCR RAW:", ocrResult);
        console.log("OCR ITEMS:", ocrItems);
        console.log("TOKEN ITEMS:", tokenItems);
        console.log("GRID ROWS:", parsedRows);

        setRawDebugText((prev) => {
          return `${prev}\n\n----- ${file.name} RAW RESULT -----\n${rawResultText}`;
        });

        setDebugText((prev) => {
          return `${prev}\n\n----- ${file.name} GRID ROWS -----\n${debugGridText}`;
        });

        for (const row of parsedRows) {
          const familyName = row["Family Name"];
          if (!familyName) continue;

          const key = normalizeFamilyKey(familyName);
          const existing = unique.get(key);

          if (!existing || scoreRow(row) > scoreRow(existing)) {
            unique.set(key, row);
          }
        }
      }

      const finalRows = Array.from(unique.values());

      setRows(finalRows);
      setStatus(
        `Done. Parsed ${totalParsedRows} row(s), found ${finalRows.length} unique family name(s).`
      );
    } catch (error) {
      console.error(error);
      setStatus("Something went wrong while processing the screenshot.");
    } finally {
      setProcessing(false);
    }
  }

  function buildRowsFromPositionedTokens(tokens, canvasWidth) {
    const usableTokens = tokens
      .map((token) => ({
        ...token,
        columnIndex:
          token.columnIndex ?? getColumnIndexFromX(token.x, canvasWidth),
      }))
      .filter((token) => {
        const text = String(token.text || "").trim();
        if (!text) return false;

        const lower = text.toLowerCase();

        if (lower.includes("family")) return false;
        if (lower.includes("node")) return false;
        if (lower.includes("victory")) return false;
        if (lower.includes("result")) return false;
        if (lower === "win") return false;

        return true;
      });

    const rowGroups = groupTokensByRows(usableTokens);
    const rows = [];

    for (const group of rowGroups) {
      const row = emptyRow();
      const cells = new Map();

      for (const token of group) {
        const columnIndex =
          token.columnIndex ?? getColumnIndexFromX(token.x, canvasWidth);

        if (!cells.has(columnIndex)) {
          cells.set(columnIndex, []);
        }

        cells.get(columnIndex).push(token);
      }

      for (let columnIndex = 0; columnIndex < COLUMNS.length; columnIndex++) {
        const column = COLUMNS[columnIndex];
        const cellTokens = cells.get(columnIndex) || [];

        if (!cellTokens.length) {
          row[column] = "";
          continue;
        }

        if (columnIndex === 0) {
          const namePieces = cellTokens
            .sort((a, b) => a.x - b.x)
            .map((token) => token.text)
            .filter((text) => /[a-zA-Z]/.test(text))
            .filter((text) => cleanFamilyName(text).length <= 22);

          row[column] = cleanFamilyName(namePieces.join(""));
        } else {
          const value = chooseBestValueForColumn(
            cellTokens,
            column,
            columnIndex,
            canvasWidth
          );

          row[column] = repairValueByColumn(value, column);
        }
      }

      if (!row["Family Name"]) continue;

      const filledStats = NUMERIC_COLUMNS.filter((column) => {
        return String(row[column] || "").trim() !== "";
      }).length;

      if (filledStats < 3) continue;

      rows.push(row);
    }

    return rows;
  }

  function groupTokensByRows(tokens) {
    const sorted = [...tokens].sort((a, b) => a.y - b.y);
    const groups = [];

    const heights = sorted
      .map((token) => token.height)
      .filter((height) => Number.isFinite(height) && height > 0);

    const medianHeight = getMedian(heights) || 18;
    const yTolerance = Math.max(7, Math.min(18, medianHeight * 0.6));

    for (const token of sorted) {
      let bestGroup = null;
      let bestDistance = Infinity;

      for (const group of groups) {
        const avgY = group.reduce((sum, item) => sum + item.y, 0) / group.length;
        const distance = Math.abs(token.y - avgY);

        if (distance <= yTolerance && distance < bestDistance) {
          bestGroup = group;
          bestDistance = distance;
        }
      }

      if (bestGroup) {
        bestGroup.push(token);
      } else {
        groups.push([token]);
      }
    }

    return groups.filter((group) => group.length >= 4);
  }

  function getColumnIndexFromX(x, canvasWidth) {
    const xPercent = x / canvasWidth;

    let bestIndex = 0;
    let bestDistance = Infinity;

    for (let i = 0; i < COLUMN_CENTERS.length; i++) {
      const distance = Math.abs(xPercent - COLUMN_CENTERS[i]);

      if (distance < bestDistance) {
        bestIndex = i;
        bestDistance = distance;
      }
    }

    return bestIndex;
  }

  function chooseBestValueForColumn(tokens, column, columnIndex, canvasWidth) {
    const candidates = [];

    for (const token of tokens) {
      const cleaned = cleanStatToken(token.text);
      if (!cleaned) continue;

      const splitValues = splitPossibleMergedStat(cleaned);

      for (const splitValue of splitValues) {
        if (!splitValue) continue;

        candidates.push({
          value: splitValue,
          x: token.x,
          distance: Math.abs(
            token.x / canvasWidth - COLUMN_CENTERS[columnIndex]
          ),
        });
      }
    }

    if (!candidates.length) return "";

    const validCandidates = candidates.filter((candidate) =>
      looksValidForColumn(candidate.value, column)
    );

    const pool = validCandidates.length ? validCandidates : candidates;

    return pickBestCandidate(pool, column);
  }

  function pickBestCandidate(candidates, column) {
    if (!candidates.length) return "";

    const sortedByPosition = [...candidates].sort(
      (a, b) => a.distance - b.distance
    );

    if (column.includes("Time")) {
      const timeCandidate = sortedByPosition.find((candidate) =>
        /^\d{1,2}:\d{2}$/.test(candidate.value)
      );

      return timeCandidate?.value || sortedByPosition[0].value;
    }

    if (MAGNITUDE_COLUMNS.has(column)) {
      /*
        Position matters more than length. This prevents 175.3K from taking
        Damage Done when 84386 is closer to the Damage Done column.
      */
      const validMagnitudeColumnValues = sortedByPosition.filter((candidate) =>
        /^\d+(\.\d+)?[KM]?$/.test(candidate.value)
      );

      if (validMagnitudeColumnValues.length) {
        return validMagnitudeColumnValues[0].value;
      }
    }

    const numericCandidate = sortedByPosition.find((candidate) =>
      /^\d+$/.test(candidate.value)
    );

    if (numericCandidate) return numericCandidate.value;

    return sortedByPosition[0].value;
  }

  function looksValidForColumn(value, column) {
    if (!value) return false;

    if (column.includes("Time")) {
      return /^\d{1,2}:\d{2}$/.test(value);
    }

    if (MAGNITUDE_COLUMNS.has(column)) {
      return /^\d+(\.\d+)?[KM]?$/.test(value);
    }

    return /^\d+$/.test(value);
  }

  function explodeItemsIntoTokens(items, canvasWidth) {
    const exploded = [];

    for (const item of items) {
      const rawText = String(item.text || "").trim();
      if (!rawText) continue;

      const cleanedText = cleanOcrTextForTokenizing(rawText);
      const pieces = splitTextIntoPieces(cleanedText);

      if (!pieces.length) continue;

      const spanColumns = getColumnsCoveredByBox(item, canvasWidth);

      /*
        If PaddleOCR gives one wide box containing multiple stats,
        place each piece into the columns covered by that box.
      */
      if (
        pieces.length > 1 &&
        spanColumns.length >= pieces.length &&
        hasNumericPiece(pieces)
      ) {
        for (let i = 0; i < pieces.length; i++) {
          const columnIndex = spanColumns[i];
          const x = COLUMN_CENTERS[columnIndex] * canvasWidth;

          exploded.push({
            ...item,
            text: pieces[i],
            x,
            columnIndex,
          });
        }

        continue;
      }

      /*
        Fallback: distribute pieces across the OCR box by character position.
      */
      const totalChars = pieces.reduce((sum, piece) => sum + piece.length, 0);
      let runningChars = 0;

      for (const piece of pieces) {
        const pieceChars = piece.length;
        const midChar = runningChars + pieceChars / 2;
        const ratio = totalChars > 0 ? midChar / totalChars : 0.5;

        const estimatedX =
          item.minX !== undefined && item.maxX !== undefined
            ? item.minX + (item.maxX - item.minX) * ratio
            : item.x;

        const columnIndex = getColumnIndexFromX(estimatedX, canvasWidth);

        exploded.push({
          ...item,
          text: piece,
          x: estimatedX,
          columnIndex,
        });

        runningChars += pieceChars;
      }
    }

    return dedupeTokenItems(exploded);
  }

  function getColumnsCoveredByBox(item, canvasWidth) {
    if (item.minX === undefined || item.maxX === undefined) {
      return [getColumnIndexFromX(item.x, canvasWidth)];
    }

    const minPercent = item.minX / canvasWidth;
    const maxPercent = item.maxX / canvasWidth;

    const covered = [];

    for (let i = 0; i < COLUMN_CENTERS.length; i++) {
      const center = COLUMN_CENTERS[i];

      if (center >= minPercent - 0.01 && center <= maxPercent + 0.01) {
        covered.push(i);
      }
    }

    if (!covered.length) {
      covered.push(getColumnIndexFromX(item.x, canvasWidth));
    }

    return covered;
  }

  function hasNumericPiece(pieces) {
    return pieces.some((piece) => /\d/.test(piece));
  }

  function splitTextIntoPieces(text) {
    const normalized = String(text || "")
      .replace(/\s+/g, " ")
      .trim();

    if (!normalized) return [];

    const roughPieces = normalized.split(" ").filter(Boolean);
    const finalPieces = [];

    for (const piece of roughPieces) {
      const subPieces = splitPossibleMergedStat(piece);

      if (subPieces.length > 1) {
        finalPieces.push(...subPieces);
        continue;
      }

      const matchPieces =
        piece.match(
          /[A-Za-z_]+|\d{1,2}:\d{2}|\d+\.\d+[KM]?|\d+[KM]|\d+|[KM]/g
        ) || [];

      finalPieces.push(...matchPieces);
    }

    return finalPieces.filter(Boolean);
  }

  function splitPossibleMergedStat(value) {
    let text = String(value || "").trim();

    if (!text) return [];

    for (const noise of HIDDEN_TIME_NOISE) {
      text = text.replaceAll(noise, "");
    }

    text = text.replace(/(\d{1,2}:\d)%/g, "$10");
    text = text.replace(/(\d{1,2}:\d{2})(\d{1,2}:\d{2})/g, "$1 $2");

    if (text.includes(" ")) {
      return text.split(/\s+/).filter(Boolean);
    }

    const mergedPlainPlusMagnitude = text.match(
      /^(\d{4,6})(\d{2,4}\.\d[KM])$/
    );

    if (mergedPlainPlusMagnitude) {
      return [mergedPlainPlusMagnitude[1], mergedPlainPlusMagnitude[2]];
    }

    const mergedMagnitudePlusPlain = text.match(/^(\d+\.\d[KM])(\d{3,6})$/);

    if (mergedMagnitudePlusPlain) {
      return [mergedMagnitudePlusPlain[1], mergedMagnitudePlusPlain[2]];
    }

    const mergedTwoMagnitudes = text.match(/^(\d+\.\d[KM])(\d+\.\d[KM])$/);

    if (mergedTwoMagnitudes) {
      return [mergedTwoMagnitudes[1], mergedTwoMagnitudes[2]];
    }

    const mergedTwoPlainLongNumbers = text.match(/^(\d{5})(\d{5})$/);

    if (mergedTwoPlainLongNumbers) {
      return [mergedTwoPlainLongNumbers[1], mergedTwoPlainLongNumbers[2]];
    }

    const pattern =
      /\d{1,2}:\d{2}|\d+\.\d+[KM]|\d+[KM]|\d+\.\d+|\d+/g;

    const matches = text.match(pattern);

    if (matches && matches.join("") === text) {
      return matches;
    }

    return [text];
  }

  function dedupeOcrItems(items) {
    const sorted = [...items].sort((a, b) => {
      const areaA = (a.width || 1) * (a.height || 1);
      const areaB = (b.width || 1) * (b.height || 1);

      return areaA - areaB;
    });

    const kept = [];

    for (const item of sorted) {
      const text = String(item.text || "").trim();
      if (!text) continue;

      const duplicate = kept.some((existing) => {
        const sameText =
          normalizeText(existing.text) === normalizeText(item.text);

        const closeCenter =
          Math.abs(existing.x - item.x) < 10 &&
          Math.abs(existing.y - item.y) < 10;

        const overlap = getBoxOverlapRatio(existing, item) > 0.75;

        return sameText && (closeCenter || overlap);
      });

      if (!duplicate) {
        kept.push(item);
      }
    }

    return kept;
  }

  function dedupeTokenItems(tokens) {
    const unique = new Map();

    for (const token of tokens) {
      const key = [
        normalizeText(token.text),
        Math.round(token.x / 7),
        Math.round(token.y / 7),
        token.columnIndex,
      ].join("|");

      const existing = unique.get(key);

      if (!existing) {
        unique.set(key, token);
        continue;
      }

      if (String(token.text).length > String(existing.text).length) {
        unique.set(key, token);
      }
    }

    return Array.from(unique.values());
  }

  function getBoxOverlapRatio(a, b) {
    if (
      a.minX === undefined ||
      a.maxX === undefined ||
      a.minY === undefined ||
      a.maxY === undefined ||
      b.minX === undefined ||
      b.maxX === undefined ||
      b.minY === undefined ||
      b.maxY === undefined
    ) {
      return 0;
    }

    const xOverlap = Math.max(
      0,
      Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX)
    );

    const yOverlap = Math.max(
      0,
      Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY)
    );

    const intersection = xOverlap * yOverlap;
    const areaA = Math.max(1, (a.maxX - a.minX) * (a.maxY - a.minY));
    const areaB = Math.max(1, (b.maxX - b.minX) * (b.maxY - b.minY));

    return intersection / Math.min(areaA, areaB);
  }

  function normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .trim();
  }

  function extractPaddleItems(result) {
    const rawItems = findLikelyOcrItems(result);

    return rawItems
      .map((entry) => {
        const text = extractTextFromEntry(entry);
        const box = extractBoxFromEntry(entry);
        const coords = normalizeBox(box);

        if (!text) return null;

        if (!coords.length) {
          return {
            text,
            x: 0,
            y: 0,
            width: 0,
            height: 0,
          };
        }

        const xs = coords.map((point) => point.x);
        const ys = coords.map((point) => point.y);

        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        return {
          text,
          x: (minX + maxX) / 2,
          y: (minY + maxY) / 2,
          width: maxX - minX,
          height: maxY - minY,
          minX,
          maxX,
          minY,
          maxY,
        };
      })
      .filter(Boolean);
  }

  function removeBadLargeOcrBoxes(items) {
    const normalHeights = items
      .map((item) => item.height)
      .filter((height) => Number.isFinite(height) && height > 0)
      .sort((a, b) => a - b);

    const medianHeight =
      normalHeights[Math.floor(normalHeights.length / 2)] || 20;

    return items.filter((item) => {
      const text = String(item.text || "").trim();

      if (!text) return false;

      /*
        Removes giant accidental name-column captures like:
        MlckeyShinryukyBGearModelDryptoRainOfHatred...
      */
      if (/^[A-Za-z_]+$/.test(text) && text.length > 24) {
        return false;
      }

      if (item.height > medianHeight * 2.2 && text.length > 18) {
        return false;
      }

      const wordCount = text.split(/\s+/).filter(Boolean).length;

      if (wordCount > 8) {
        return false;
      }

      return true;
    });
  }

  function findLikelyOcrItems(result) {
    const candidates = [];

    walkForOcrItems(result, candidates);

    return candidates.filter((item) => {
      return normalizeBox(extractBoxFromEntry(item)).length > 0;
    });
  }

  function walkForOcrItems(value, output) {
    if (!value) return;

    if (Array.isArray(value)) {
      if (looksLikeOcrTuple(value)) {
        output.push(value);
        return;
      }

      for (const item of value) {
        walkForOcrItems(item, output);
      }

      return;
    }

    if (typeof value === "object") {
      if (looksLikeOcrObject(value)) {
        output.push(value);
        return;
      }

      for (const key of Object.keys(value)) {
        walkForOcrItems(value[key], output);
      }
    }
  }

  function looksLikeOcrTuple(value) {
    if (!Array.isArray(value)) return false;

    const first = value[0];
    const second = value[1];

    const firstLooksLikeBox =
      Array.isArray(first) &&
      first.length >= 4 &&
      Array.isArray(first[0]) &&
      first[0].length >= 2;

    const secondLooksLikeText =
      typeof second === "string" ||
      typeof second?.[0] === "string" ||
      typeof second?.text === "string";

    return firstLooksLikeBox && secondLooksLikeText;
  }

  function looksLikeOcrObject(value) {
    if (!value || typeof value !== "object") return false;

    const hasText =
      typeof value.text === "string" ||
      typeof value.label === "string" ||
      typeof value.words === "string" ||
      typeof value.value === "string" ||
      typeof value.recText === "string" ||
      typeof value.description === "string";

    const hasBox =
      value.box ||
      value.points ||
      value.bbox ||
      value.frame ||
      value.position ||
      value.rect;

    return Boolean(hasText && hasBox);
  }

  function extractTextFromEntry(entry) {
    if (!entry) return "";

    if (typeof entry === "string") return entry.trim();

    if (Array.isArray(entry)) {
      if (typeof entry[1] === "string") return entry[1].trim();
      if (typeof entry[1]?.[0] === "string") return entry[1][0].trim();
      if (typeof entry[1]?.text === "string") return entry[1].text.trim();
    }

    if (typeof entry === "object") {
      const text =
        entry.text ??
        entry.label ??
        entry.words ??
        entry.value ??
        entry.recText ??
        entry.description ??
        "";

      return String(text || "").trim();
    }

    return "";
  }

  function extractBoxFromEntry(entry) {
    if (!entry) return [];

    if (Array.isArray(entry)) {
      return entry[0] ?? [];
    }

    if (typeof entry === "object") {
      return (
        entry.box ??
        entry.points ??
        entry.bbox ??
        entry.frame ??
        entry.position ??
        entry.rect ??
        []
      );
    }

    return [];
  }

  function normalizeBox(box) {
    if (!box) return [];

    if (
      Array.isArray(box) &&
      box.length >= 4 &&
      Array.isArray(box[0]) &&
      box[0].length >= 2
    ) {
      return box.map((point) => ({
        x: Number(point[0]),
        y: Number(point[1]),
      }));
    }

    if (Array.isArray(box) && box.length >= 4 && typeof box[0] === "number") {
      const [x0, y0, x1, y1] = box;

      return [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: y1 },
        { x: x0, y: y1 },
      ];
    }

    if (
      typeof box === "object" &&
      box.x0 !== undefined &&
      box.y0 !== undefined &&
      box.x1 !== undefined &&
      box.y1 !== undefined
    ) {
      return [
        { x: Number(box.x0), y: Number(box.y0) },
        { x: Number(box.x1), y: Number(box.y0) },
        { x: Number(box.x1), y: Number(box.y1) },
        { x: Number(box.x0), y: Number(box.y1) },
      ];
    }

    if (
      typeof box === "object" &&
      box.left !== undefined &&
      box.top !== undefined &&
      box.width !== undefined &&
      box.height !== undefined
    ) {
      const x0 = Number(box.left);
      const y0 = Number(box.top);
      const x1 = x0 + Number(box.width);
      const y1 = y0 + Number(box.height);

      return [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: y1 },
        { x: x0, y: y1 },
      ];
    }

    return [];
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;

      reader.readAsDataURL(file);
    });
  }

  function prepareImageForOCR(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        const crop = autoCropTableArea(img);

        const scale = 3;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: true });

        canvas.width = Math.round(crop.width * scale);
        canvas.height = Math.round(crop.height * scale);

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        ctx.drawImage(
          img,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          0,
          0,
          canvas.width,
          canvas.height
        );

        improveContrast(canvas);

        resolve(canvas);
      };

      img.src = dataUrl;
    });
  }

  function autoCropTableArea(img) {
    const aspectRatio = img.height / img.width;
    const looksAlreadyCropped = aspectRatio < 0.42;

    if (looksAlreadyCropped) {
      return {
        x: img.width * 0.0,
        y: img.height * 0.0,
        width: img.width * 1.0,
        height: img.height * 1.0,
      };
    }

    return {
      x: img.width * 0.008,
      y: img.height * 0.185,
      width: img.width * 0.984,
      height: img.height * 0.815,
    };
  }

  function improveContrast(canvas) {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      const avg = (r + g + b) / 3;

      let value = 255 - avg;
      value = (value - 115) * 2.2 + 128;

      if (value < 0) value = 0;
      if (value > 255) value = 255;

      data[i] = value;
      data[i + 1] = value;
      data[i + 2] = value;
    }

    ctx.putImageData(imageData, 0, 0);
  }

  function cleanOcrTextForTokenizing(value) {
    let text = String(value || "").trim();

    for (const noise of HIDDEN_TIME_NOISE) {
      text = text.replaceAll(noise, "");
    }

    text = text.replace(/,/g, ".");
    text = text.replace(/\//g, "7");

    return text;
  }

  function cleanFamilyName(value) {
    return String(value || "")
      .trim()
      .replace(/[^a-zA-Z0-9_]/g, "");
  }

  function normalizeFamilyKey(value) {
    return cleanFamilyName(value).toLowerCase();
  }

  function cleanStatToken(value) {
    let text = String(value || "").trim();

    if (!text) return "";

    for (const noise of HIDDEN_TIME_NOISE) {
      text = text.replaceAll(noise, "");
    }

    text = text.replace(/,/g, ".");

    text = text.replace(/O/g, "0");
    text = text.replace(/o/g, "0");
    text = text.replace(/I/g, "1");
    text = text.replace(/l/g, "1");
    text = text.replace(/\[/g, "1");
    text = text.replace(/\]/g, "1");
    text = text.replace(/B/g, "8");
    text = text.replace(/S/g, "5");
    text = text.replace(/b/g, "6");
    text = text.replace(/\//g, "7");

    text = text.replace(/[^0-9.:KM]/g, "");

    return text;
  }

  function repairValueByColumn(value, column) {
    let text = String(value || "").trim();

    if (!text) return "";

    if (column.includes("Time")) {
      return normalizeTime(text);
    }

    if (MAGNITUDE_COLUMNS.has(column)) {
      if (/^\d+\.\d+$/.test(text)) {
        text = `${text}K`;
      }

      return text;
    }

    return text;
  }

  function normalizeTime(value) {
    const match = String(value || "").match(/^(\d{1,2}):(\d{2})$/);

    if (!match) return String(value || "");

    const minutes = match[1].padStart(2, "0");
    const seconds = match[2];

    return `${minutes}:${seconds}`;
  }

  function getMedian(values) {
    if (!values.length) return null;

    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }

  function emptyRow() {
    const row = {};

    for (const column of COLUMNS) {
      row[column] = "";
    }

    return row;
  }

  function scoreRow(row) {
    let score = 0;

    for (const column of COLUMNS) {
      const value = String(row[column] || "").trim();

      if (!value) continue;

      score += 1;

      if (/[KM]$/.test(value)) score += 0.25;
      if (/^\d{2}:\d{2}$/.test(value)) score += 0.25;
    }

    return score;
  }

  function isSuspiciousCell(column, value) {
    const text = String(value || "").trim();

    if (!text) return false;

    if (column === "CC's") {
      const num = Number(text);

      if (!Number.isNaN(num) && num > 250) return true;
    }

    if (column.includes("Time")) {
      return !/^\d{2}:\d{2}$/.test(text);
    }

    return false;
  }

  function safeStringify(value) {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  function updateCell(rowIndex, column, value) {
    setRows((prev) => {
      const copy = [...prev];

      copy[rowIndex] = {
        ...copy[rowIndex],
        [column]: value,
      };

      return copy;
    });
  }

  function exportExcel() {
    if (!rows.length) return;

    const worksheet = XLSX.utils.json_to_sheet(rows, {
      header: COLUMNS,
    });

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Node War Results");

    XLSX.writeFile(workbook, "node_war_results.xlsx");
  }

  function clearRows() {
    setRows([]);
    setStatus("Upload screenshots to begin.");
    setPreviewImage(null);
    setDebugText("");
    setRawDebugText("");
  }

  return (
    <div style={{ padding: 24, maxWidth: 1500, margin: "0 auto" }}>
      <h1>Node War OCR Exporter - PaddleOCR Grid Test</h1>

      <p>
        Upload one or more screenshots. This version splits merged PaddleOCR
        boxes and assigns each value to the closest table column by position.
      </p>

      <input
        type="file"
        accept="image/*"
        multiple
        disabled={processing || !ocrReady}
        onChange={handleFiles}
      />

      <div style={{ marginTop: 16 }}>
        <button onClick={exportExcel} disabled={!rows.length || processing}>
          Export Excel
        </button>

        <button
          onClick={clearRows}
          disabled={processing}
          style={{ marginLeft: 8 }}
        >
          Clear
        </button>

        <button
          onClick={() => setShowDebug((prev) => !prev)}
          disabled={processing}
          style={{ marginLeft: 8 }}
        >
          {showDebug ? "Hide Debug" : "Show Debug"}
        </button>
      </div>

      <p style={{ marginTop: 16 }}>{status}</p>

      {previewImage && showDebug && (
        <div style={{ marginTop: 20 }}>
          <h3>OCR Preview</h3>
          <p>This is the cropped and processed image being sent to PaddleOCR.</p>
          <img
            src={previewImage}
            alt="OCR preview"
            style={{
              maxWidth: "100%",
              border: "1px solid #ccc",
              background: "#fff",
            }}
          />
        </div>
      )}

      <div style={{ overflowX: "auto", marginTop: 24 }}>
        <table
          border="1"
          cellPadding="6"
          style={{
            borderCollapse: "collapse",
            width: "100%",
            minWidth: 1500,
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {COLUMNS.map((column) => {
                  const suspicious = isSuspiciousCell(column, row[column]);

                  return (
                    <td
                      key={column}
                      style={{
                        background: suspicious ? "#fff3cd" : "transparent",
                      }}
                    >
                      <input
                        value={row[column] || ""}
                        onChange={(e) =>
                          updateCell(rowIndex, column, e.target.value)
                        }
                        style={{
                          width:
                            column === "Family Name"
                              ? 145
                              : column.includes("Time")
                              ? 90
                              : 85,
                          border: "none",
                          outline: "none",
                          background: "transparent",
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDebug && (
        <>
          {debugText && (
            <details style={{ marginTop: 24 }} open>
              <summary>Debug Grid Rows</summary>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  background: "#f4f4f4",
                  padding: 12,
                  border: "1px solid #ccc",
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                {debugText}
              </pre>
            </details>
          )}

          {rawDebugText && (
            <details style={{ marginTop: 24 }}>
              <summary>Debug Raw PaddleOCR Result</summary>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  background: "#f4f4f4",
                  padding: 12,
                  border: "1px solid #ccc",
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                {rawDebugText}
              </pre>
            </details>
          )}
        </>
      )}
    </div>
  );
}